// 3rd party libraries
import React, { ReactElement, useState, useEffect } from 'react';
import { BrowserRouter as Router, Redirect, Route, RouteProps, Switch } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import styled, { css, ThemeProvider } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from 'constants/authConfig';
import loadable from '@loadable/component';
// Sentry
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

// Constants
import { JBTRoutes } from 'constants/routes';
import { sideNavItems } from 'constants/nav';
import Footer from './components/Footer/Footer';
import Header from 'components/Header/Header';

// Components
import {
  Loader,
  PageHeader,
  PermissionWrapper,
  PowerBIEmbedded,
  SidebarNav,
  Typography,
  Test
} from 'components';

import { ToastContainer } from 'react-toastify';

// Hooks
import { useAuthB2C, usePageTracking, usePermissions } from 'hooks';

// Pages
import {
  AddNewMachine,
  Admin,
  B2CLogin,
  CheckHierarchy,
  Dashboard,
  FindMachine,
  FleetDashboardPage,
  FleetMachineDetail,
  ReportPage,
  Line,
  Login,
  //Machine,
  //MachineManagement,
  //MachinePmPartsPage,
  //MaintenanceServiceDashboard,
  MaintenanceServiceMachineDashboard,
  //PartPage,
  PartsSuggestions,
  SFOrdersPage,
  Site,
  //UserManagement,
  //UserManagementTwo,
  MTLConfigurationMapping,
  ReviewAndPublishPage,
  //DemoTable,
  AccessibilityPolicy,
  PartsCatalogPage,
  AccessDenied,
  MaintenanceSchedule,
  OnboardingPage,
  MasterTagListDashBoard,
  ProductDetailPage,
  MachineInformation
} from 'pages';

//import CreateAlertPage from 'pages/AlertsPage/CreateAlert';

// Icons
import { faBell, faTools } from '@fortawesome/free-solid-svg-icons';
import GroupIcon from 'icons/img/GroupIcon.svg';
import MachineManagementIcon from 'icons/img/MachineManagementIcon.svg';

// Theme
import theme, { themeColors } from 'themes';
import { GlobalStyle } from 'themes/GlobalStyle';

// Provider
import { LanguageProvider, FleetNavigationProvider } from 'providers';

// Helpers
import { getLanguageId, getUserLanguage, getUserRegion } from 'common/helpers/v1';
import { getPermissionNameByLocation } from 'pages/Permission/utils';

// State actions
import { useAuthToken } from 'selectors';

//import { useValidateTokenQuery } from 'api';
import { Role } from 'types';
import { AlertManagement } from 'pages/AlertManagement/AlertManagament';
import { AlertTemplateManagement } from 'pages/AlertManagement/AlertTemplateManagement';
import { AlertCreator } from 'pages/AlertCreator/AlertCreatorWelcome';
import { FleetPage } from 'common/pages/fleetV2/FleetPage';
import GroupDetails from 'pages/UserManagementTwo/components/Group/GroupDetails';
import Bell from 'icons/indexed-icons/Bell';
import CheckCircle from 'icons/indexed-icons/CheckCircle';
import Exclamation from 'icons/indexed-icons/Exclamation';
import ExclamationCircle from 'icons/indexed-icons/ExclamationCircle';
import InformationCircle from 'icons/indexed-icons/InformationCircle';
import X from 'icons/indexed-icons/X';
import UserSettings from 'components/UserSettings';
import AppDownloadHeader from 'components/AppDownloadHeader';
import AppDownload from 'components/AppDownload';
import { ConfiguredComponentsDemo } from 'common/components/ConfiguredComponents/demo/ConfiguredComponentsDemo';
import ClickWrapper from 'components/ClickWrapper/ClickWrapper';
import { ConfigurableChartDemos } from 'common/components/ConfigurableChart/demo/ConfigurableChartDemos';
//import { DisplayAllIconsContainer } from 'common/components/DisplayAllIconsContainer';

// Sentry initialization script
const history = createBrowserHistory();

const TRACE_SAMPLE_RATE = import.meta.env.VITE_ENV === 'dev' ? 1.0 : 0.2;
Sentry.init({
  debug: false,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,

  release: import.meta.env.VITE_VERSION,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(history)
    }),
    new Sentry.Integrations.Breadcrumbs({ console: false }),
    new Sentry.Replay({
      maskAllText: false,
      blockAllMedia: false
    })
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: TRACE_SAMPLE_RATE,
  replaysSessionSampleRate: 0.1, //Replay sample rate. Records random sessions without errors
  replaysOnErrorSampleRate: 1.0, // Replay on error sample rate.

  //Callback to modify event before it is sent.
  //This logs data being sent to Sentry
  beforeSend(event, hint) {
    if (hint) console.log('thanks ts');
    // Send the URL here to ensure it includes the full path without masking the id
    if (event.request && event.request.url) {
      event.request.url = window.location.href;
    }

    // Check if it is an exception, and if so, show the report dialog
    // if (event.exception?.values && event.event_id) {
    //   Sentry.showReportDialog({ eventId: event.event_id });
    // }

    //return event;
  }
});

// // Function to set user context from session storage
// function setUserContextFromSessionStorage() {
//   const userString = sessionStorage.getItem('user');
//   if (userString) {
//     try {
//       const user = JSON.parse(userString);
//       if (user.email) {
//         Sentry.setUser({
//           email: user.email
//         });
//       }
//     } catch (error) {
//       console.error('Failed to parse user from session storage:', error);
//     }
//   }
// }

// Call this function after Sentry initialization and whenever user information is available
// setUserContextFromSessionStorage();

interface MainContentProps {
  toggleContent: boolean;
}

// TODO - Split framing elements below into new components
const Frame = styled.div`
  position: relative;
  // min-height: 100vh;
  display: flex;
`;

const MainContent = styled.div<MainContentProps>`
  background-color: ${(props) => props.theme.colors.borders.border02.fill};
  flex-grow: 1;
  margin-bottom: 3.125rem;

  @media (max-width: 539px) {
    width: 100%;
    margin-left: 0;
  }

  @media (min-width: 540px) {
    ${(props) =>
      props.toggleContent
        ? css`
            width: calc(100vw - 12.325rem);
            margin-left: 4.325rem;
          `
        : css`
            width: calc(100vw - 16.35rem);
            margin-left: 16.325rem;
          `}
  }
`;

const SidebarnavLargeScreen = styled.div`
  @media (max-width: 540px) {
    display: none;
  }
`;

export const StyledToastContainer = styled(ToastContainer)`
  .custom-toast {
    width: 316px;
    padding: 12px;
    color: ${themeColors.textLightBlack};
    border-radius: 4px;
    box-shadow: 0px 4px 0px ${themeColors.primaryBlue5}, 0px 25.6px 57.6px rgba(0, 0, 0, 0.1),
      0px 4.8px 14.4px rgba(0, 0, 0, 0.18);

    .icon--x {
      margin: 5px;
    }
    .Toastify__toast {
      &-body {
        align-items: initial !important;
        padding: 0;
        .icon--information-circle {
          transform: scale(1.4) translate(1px, 1px);
        }
      }
      &-icon {
        margin-inline-end: 12px;
      }
    }
    &.Toastify__toast {
      &--error {
        background: ${themeColors.error300};
        box-shadow: 0px 4px 0px ${themeColors.error100}, 0px 25.6px 57.6px rgba(0, 0, 0, 0.1),
          0px 4.8px 14.4px rgba(0, 0, 0, 0.18);
        > svg path {
          stroke: ${themeColors.error200};
        }
      }
      &--info {
        background: ${themeColors.info300};
        box-shadow: 0px 4px 0px ${themeColors.primaryBlue5}, 0px 25.6px 57.6px rgba(0, 0, 0, 0.1),
          0px 4.8px 14.4px rgba(0, 0, 0, 0.18);
        > svg path {
          stroke: ${themeColors.primaryBlue5};
        }
      }
      &--success {
        background: #e9f5e9;
        box-shadow: 0px 4px 0px ${themeColors.green3}, 0px 25.6px 57.6px rgba(0, 0, 0, 0.1),
          0px 4.8px 14.4px rgba(0, 0, 0, 0.18);
        > svg path {
          stroke: ${themeColors.green3};
        }
      }
      &--warning {
        background: ${themeColors.warning300};
        box-shadow: 0px 4px 0px ${themeColors.warning}, 0px 25.6px 57.6px rgba(0, 0, 0, 0.1),
          0px 4.8px 14.4px rgba(0, 0, 0, 0.18);
        > svg path {
          stroke: ${themeColors.warning};
        }
      }
    }
  }
`;

interface CustomRouteProps extends RouteProps {
  children?: React.ReactNode;
}

const Machine = loadable(() => import('./pages/Machine'));
const MachineManagement = loadable(() => import('./pages/MachineManagement'));
const MachinePmPartsPage = loadable(() => import('./pages/MachinePmPartsPage'));
const MaintenanceServiceDashboard = loadable(() => import('./pages/MaintenanceServiceDashboard'));
const UserManagement = loadable(() => import('./pages/UserManagement'));
const UserManagementTwo = loadable(() => import('./pages/UserManagementTwo'));
const PartPage = loadable(() => import('./pages/PartPage'));
const CreateAlertPage = loadable(() => import('pages/AlertsPage/CreateAlert'));
const DocumentationCenter = loadable(() => import('pages/Documentation'));
const HelpPage = loadable(() => import('pages/HelpPage/HelpPage'));
const Swagger = loadable(() => import('pages/SwaggerUI'));

function MachineViewComponent() {
  return <Machine />;
}

function MachineManagementViewComponent() {
  return <MachineManagement />;
}

function MachinePmPartsPageViewComponent() {
  return <MachinePmPartsPage />;
}

function MaintenanceServiceDashboardViewComponent() {
  return <MaintenanceServiceDashboard />;
}

function UserManagementViewComponent() {
  return <UserManagement />;
}

function UserManagementTwoViewComponent() {
  return <UserManagementTwo />;
}

function PartsPageViewComponent() {
  return <PartPage />;
}

function CreateAlertPageViewComponent() {
  return <CreateAlertPage />;
}

function DocumentationCenterViewComponent() {
  return <DocumentationCenter />;
}
function HelpPageViewComponent() {
  return <HelpPage />;
}

function SwaggerViewPageComponent() {
  return <Swagger />;
}

const TrackedRoute = ({ children, ...rest }: CustomRouteProps): React.ReactElement => {
  usePageTracking();
  return <Route {...rest}>{children}</Route>;
};

const PrivateRoute = ({ children, ...rest }: CustomRouteProps): React.ReactElement => {
  const b2cflag: boolean = import.meta.env.VITE_ENABLE_B2C == 'true';
  const [authToken] = useState(useAuthToken());
  const [authPending, setAuthPending] = useState(true);
  const auth = useAuthB2C();

  useEffect(() => {
    if (b2cflag && authPending) {
      const abortController = new AbortController();
      auth.getAccessToken();
      auth.getUserResources();
      setAuthPending(false);
      return () => {
        // Function returned from useEffect is called on authToken change
        // Here it'll abort the fetch that is in auth.getUserResources();
        abortController.abort();
      };
    }
  }, [authToken]);

  return (
    <TrackedRoute
      {...rest}
      render={({ location }) => {
        if (authToken) {
          return (
            <PermissionWrapper
              page={getPermissionNameByLocation(location.pathname)}
              redirect={true}
            >
              <>{children}</>
            </PermissionWrapper>
          );
        }

        if (b2cflag && authPending) {
          return <Loader />;
        } else {
          return (
            <Redirect
              to={{
                pathname: location.pathname.includes('/documentation')
                  ? JBTRoutes.fileExpire
                  : JBTRoutes.login,
                // Populate state to redirect back here after login
                state: { from: location }
              }}
            />
          );
        }
      }}
    />
  );
};

const App = (): ReactElement => {
  const b2cflag: boolean = import.meta.env.VITE_ENABLE_B2C == 'true';
  // Hook to make sure the user session is properly expired:
  // - checks at the interval of the env var or every 30 seconds
  const { t, i18n } = useTranslation(['common']);

  // 06/17/24 Commenting lines 208-212 out because they are causing unnecessary reloads.
  // Also, I believe this was implemented before B2C and possibly not needed anymore.

  // const validateInterval = import.meta.env.VITE_VALIDATE_INTERVAL_MILLI
  //   ? parseInt(import.meta.env.VITE_VALIDATE_INTERVAL_MILLI)
  //   : 30000;

  //useValidateTokenQuery(undefined, { pollingInterval: validateInterval });

  const permission = usePermissions();
  const role = permission?.role;

  const language = i18n.language;
  const languageId = getLanguageId(i18n.language);
  const languageName = getUserLanguage(i18n.language, i18n.language);
  const languageNameInEnglish = getUserLanguage(i18n.language);
  const region = getUserRegion(i18n.language);
  const [toggleContent, setToggleContent] = useState<boolean>(false);

  return (
    <MsalProvider instance={msalInstance}>
      <Router>
        <LanguageProvider
          context={{ language, languageId, languageName, languageNameInEnglish, region }}
        >
          <ThemeProvider theme={theme}>
            {/* Global toast container */}
            <StyledToastContainer
              position="top-center"
              autoClose={4000}
              newestOnTop={true}
              closeOnClick
              hideProgressBar={true}
              pauseOnFocusLoss={false}
              pauseOnHover={false}
              limit={3}
              toastClassName="custom-toast"
              icon={({ type }) => {
                if (type === 'success') return <CheckCircle color={themeColors.green3} />;
                if (type === 'error') return <ExclamationCircle color={themeColors.error200} />;
                if (type === 'info') return <InformationCircle color={themeColors.primaryBlue5} />;
                if (type === 'warning') return <Exclamation color={themeColors.warning} />;
                else return <Bell color={themeColors.primaryBlue5} />;
              }}
              closeButton={<X />}
            />
            <GlobalStyle />
            {/* <IosAppDownloadHeader/> */}
            <AppDownload />
            <AppDownloadHeader />
            <Header items={sideNavItems} role={role} />
            <ClickWrapper />
            <Frame className="page-frame">
              <Switch>
                <TrackedRoute path={JBTRoutes.login}>
                  {b2cflag ? <B2CLogin /> : <Login />}
                </TrackedRoute>

                <TrackedRoute path={JBTRoutes.accessDenied}>
                  <AccessDenied />
                </TrackedRoute>
                <TrackedRoute path={JBTRoutes.fileExpire}>
                  <B2CLogin />
                </TrackedRoute>

                <Route path="/charts-demo" render={() => <ConfigurableChartDemos />} />
                <Route
                  path="/dancing-queen"
                  render={() => {
                    return <ConfiguredComponentsDemo />;
                  }}
                />

                <PrivateRoute path="/">
                  <FleetNavigationProvider>
                    <SidebarnavLargeScreen>
                      <SidebarNav
                        items={sideNavItems}
                        role={role}
                        toggleContent={toggleContent}
                        setToggleContent={setToggleContent}
                      />
                    </SidebarnavLargeScreen>
                    <MainContent toggleContent={toggleContent}>
                      <Switch>
                        {/* Machine  */}
                        <TrackedRoute path={JBTRoutes.machineV2}>
                          <FleetPage />
                        </TrackedRoute>

                        {/* Line V2*/}
                        <TrackedRoute path={JBTRoutes.lineV2}>
                          <FleetPage />
                        </TrackedRoute>

                        {/* Site V2*/}
                        <TrackedRoute path={JBTRoutes.siteV2}>
                          <FleetPage />
                        </TrackedRoute>

                        <Route
                          exact
                          path="/"
                          render={() => {
                            return <Redirect to={JBTRoutes.dashboard} />;
                          }}
                        />
                        {/* Notifications */}
                        <TrackedRoute path={JBTRoutes.notifications}>
                          <PageHeader
                            heading={t('notifications')}
                            icon={{ iconElement: faBell, iconType: 'fa' }}
                          />
                          <Test />
                          <Typography variant="h1">Coming soon</Typography>
                        </TrackedRoute>

                        {/* Fleet */}
                        <TrackedRoute exact path={JBTRoutes.fleet}>
                          <FleetDashboardPage />
                        </TrackedRoute>

                        <TrackedRoute exact path={JBTRoutes.fleetMachine}>
                          <FleetMachineDetail />
                        </TrackedRoute>

                        {/*Master tag list*/}
                        <TrackedRoute exact path={JBTRoutes.machineMasterTagListDashBoard}>
                          <MasterTagListDashBoard toggleContent={toggleContent} />
                        </TrackedRoute>

                        {/* Site */}
                        <TrackedRoute path={JBTRoutes.site}>
                          <Site />
                        </TrackedRoute>

                        {/* Line */}
                        <TrackedRoute path={JBTRoutes.line}>
                          <Line />
                        </TrackedRoute>

                        {/* Machine  */}
                        <TrackedRoute path={JBTRoutes.machine}>
                          {MachineViewComponent()}
                        </TrackedRoute>

                        {/* Maintenance */}
                        <TrackedRoute
                          path={[
                            JBTRoutes.maintenanceServicemachine,
                            JBTRoutes.maintenanceServicemachinePlanned,
                            JBTRoutes.maintenanceServicemachineHighPriority,
                            JBTRoutes.maintenanceServicemachineHighPriorityPMPlanning,
                            JBTRoutes.maintenanceServicemachineHighPriorityServiceTech,
                            JBTRoutes.maintenanceServicemachineHighPriorityReports,
                            JBTRoutes.maintenanceServicemachineHighPriorityPredictive,
                            JBTRoutes.maintenanceServicemachineHistory
                          ]}
                        >
                          <MaintenanceServiceMachineDashboard />
                        </TrackedRoute>
                        <TrackedRoute
                          path={[
                            JBTRoutes.maintenanceService,
                            JBTRoutes.maintenanceServiceHighPriorityPMPlanning,
                            JBTRoutes.maintenanceServiceHighPriorityPredictive,
                            JBTRoutes.maintenanceServiceHighPriorityReports,
                            JBTRoutes.maintenanceServiceHighPriorityServiceTech,
                            JBTRoutes.maintenanceServicePlanned,
                            JBTRoutes.maintenanceServiceHistory
                          ]}
                        >
                          {MaintenanceServiceDashboardViewComponent()}
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.maintenanceServiceevents}>
                          MAINTENANCE - SERVICE EVENTS
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.maintenanceRoutine}>
                          MAINTENANCE - ROUTINE MAINTENANCE
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.maintenanceManuals}>
                          <PageHeader
                            heading="Manuals and guides"
                            icon={{ iconElement: faTools, iconType: 'fa' }}
                          />
                          <Typography variant="h1">Coming soon</Typography>
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.maintenanceRemotediagnostics}>
                          MAINTENANCE - REMOTE DIAGNOSTICS
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.maintenanceMachinepmparts}>
                          {MachinePmPartsPageViewComponent()}
                        </TrackedRoute>
                        <Route
                          exact
                          path={JBTRoutes.maintenance}
                          render={() => {
                            return <Redirect to={JBTRoutes.maintenanceService} />;
                          }}
                        />

                        {/* Parts */}
                        <TrackedRoute exact path={JBTRoutes.partsCatalog}>
                          <PartsCatalogPage />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.partsProduct}>
                          <ProductDetailPage />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.partsMachine}>
                          {PartsPageViewComponent()}
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.machineDocumentation}>
                          {DocumentationCenterViewComponent()}
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.partsSuggestions}>
                          <PartsSuggestions />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.partsLeadtimes}>
                          PARTS - LEAD TIMES
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.partsOrders}>
                          <SFOrdersPage />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.partsInventory}>
                          PARTS - INVENTORY
                        </TrackedRoute>
                        <Route
                          exact
                          path={JBTRoutes.parts}
                          render={() => {
                            return <Redirect to={JBTRoutes.partsCatalog} />;
                          }}
                        />
                        {/* AddNewMachine */}
                        {role && role === Role.Admin && (
                          <TrackedRoute path={JBTRoutes.adminAddNewMachine}>
                            <AddNewMachine />
                          </TrackedRoute>
                        )}

                        {/* Admin */}
                        {role && role === Role.Admin && (
                          <TrackedRoute path={JBTRoutes.admin}>
                            <Admin />
                          </TrackedRoute>
                        )}

                        {/* iOPS */}
                        <TrackedRoute exact path={JBTRoutes.iops}>
                          <PowerBIEmbedded />
                        </TrackedRoute>
                        {/* User Management */}
                        <TrackedRoute path={JBTRoutes.userManagement}>
                          <PageHeader
                            mb="0rem"
                            heading={t('card_group_user_management')}
                            icon={GroupIcon}
                          />
                          {UserManagementViewComponent()}
                        </TrackedRoute>
                        {/* User Management v2*/}
                        <TrackedRoute exact path={JBTRoutes.userManagementTwo}>
                          {UserManagementTwoViewComponent()}
                        </TrackedRoute>
                        <TrackedRoute exact path={JBTRoutes.userManagementTwoUsers}>
                          {UserManagementTwoViewComponent()}
                        </TrackedRoute>
                        <TrackedRoute
                          path={[JBTRoutes.userSettingsBasic, JBTRoutes.userSettingsNotification]}
                        >
                          <UserSettings />
                        </TrackedRoute>
                        <TrackedRoute exact path={JBTRoutes.groupDetails}>
                          <PageHeader
                            mb="0rem"
                            heading={t('card_group_user_management')}
                            icon={GroupIcon}
                          />
                          <GroupDetails />
                        </TrackedRoute>

                        {/* Machine Management */}
                        <TrackedRoute path={JBTRoutes.machineManagement}>
                          <PageHeader
                            mb="2rem"
                            heading={t('card_machine_management')}
                            icon={MachineManagementIcon}
                          />
                          {MachineManagementViewComponent()}
                        </TrackedRoute>

                        <TrackedRoute path={JBTRoutes.machineManagementTagTemplate}>
                          <PageHeader
                            mb="2rem"
                            heading={t('card_machine_management')}
                            icon={MachineManagementIcon}
                          />
                          {MachineManagementViewComponent()}
                        </TrackedRoute>

                        <TrackedRoute path={JBTRoutes.documentation}>
                          {DocumentationCenterViewComponent()}
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.machineAsset}>
                          <MachineInformation />
                        </TrackedRoute>
                        {/* Alert Management */}
                        {role && role === Role.Admin && (
                          <TrackedRoute path={JBTRoutes.alertManagement}>
                            <AlertManagement />
                          </TrackedRoute>
                        )}
                        {/* Alert Creator */}
                        {role && (
                          <TrackedRoute path={JBTRoutes.alertCreator}>
                            <AlertCreator />
                          </TrackedRoute>
                        )}
                        {/* Alert Management */}
                        {role && role === Role.Admin && (
                          <TrackedRoute path={JBTRoutes.alertTemplateManagement}>
                            <AlertTemplateManagement />
                          </TrackedRoute>
                        )}

                        {/* Demo Table */}
                        {/* {role && role === Role.Admin && (
                          <TrackedRoute path={JBTRoutes.demoTable}>
                            <DemoTable />
                          </TrackedRoute>
                        )} */}

                        <TrackedRoute path={JBTRoutes.machineManagementNew}>
                          <FindMachine />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.machineManagementHierarchy}>
                          <CheckHierarchy />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.machineManagementMtlConfigurationMapping}>
                          <MTLConfigurationMapping />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.machineReviewAndPublish}>
                          <ReviewAndPublishPage />
                        </TrackedRoute>
                        {/* Customer Reports */}
                        <TrackedRoute path={JBTRoutes.customerReports}>
                          <ReportPage />
                        </TrackedRoute>
                        {/* Help */}
                        <TrackedRoute path={JBTRoutes.help}>
                          {/*<Typography variant="h1">Coming soon</Typography>*/}
                          {HelpPageViewComponent()}
                        </TrackedRoute>
                        {/* Accesibility Policy */}
                        <TrackedRoute path={JBTRoutes.accessibilityPolicy}>
                          <AccessibilityPolicy />
                          {/*<Typography variant="h1">Coming soon</Typography>*/}
                        </TrackedRoute>
                        {/* App Settings */}
                        {/* <TrackedRoute path={JBTRoutes.settings}>
                          <Settings />
                        </TrackedRoute> */}
                        {/* Dashboard */}
                        <TrackedRoute path={JBTRoutes.dashboard}>
                          <Dashboard />
                        </TrackedRoute>
                        {/* New Asset Onboarding Page */}
                        <TrackedRoute path={JBTRoutes.onboardingMaintenanceSchedulePage}>
                          <MaintenanceSchedule />
                        </TrackedRoute>
                        <TrackedRoute path={JBTRoutes.onboardingPage}>
                          <OnboardingPage />
                        </TrackedRoute>

                        <TrackedRoute path={JBTRoutes.swaggerPage}>
                          {SwaggerViewPageComponent()}
                        </TrackedRoute>

                        {/* Create Alert Page */}
                        <TrackedRoute path={JBTRoutes.createAlert}>
                          {CreateAlertPageViewComponent()}
                        </TrackedRoute>

                        {/* {process.env.NODE_ENV === 'development' && (
                          <TrackedRoute path={JBTRoutes.showProjectIcons}>
                            <DisplayAllIconsContainer />
                          </TrackedRoute>
                        )} */}
                      </Switch>
                    </MainContent>
                  </FleetNavigationProvider>
                  {/* Commenting the below components as they are already present in Header section */}
                  {/* <ShowLanguages />
                  <SavedProductList />
                  <ContextAwareCart /> */}
                </PrivateRoute>
              </Switch>
            </Frame>

            <Footer toggleContent={toggleContent} />
          </ThemeProvider>
        </LanguageProvider>
      </Router>
    </MsalProvider>
  );
};

export default App;
